import { dedupeChatsByConversationKey } from "./dedupeChatsByConversationKey";

/**
 * Helpers that wrap {@link dedupeChatsByConversationKey} for the "given an id
 * (or list of ids) pointing at a chat seat, surface the viewer's own seat for
 * the same external conversation" case.
 *
 * Why this exists: group fan-out gives every member of a group their own seat
 * row for the same external JID / phone number. Pre-orders persist only the
 * *trigger user's* seat in `whatsapp_chat_id` / `sms_chat_id`, and the
 * junction-derived `linked_*_chat_ids` lists every member's seat. When the
 * viewer opens a chat from the recap, we want them on **their own** seat so
 * (a) the popover doesn't expose duplicate sibling choices and (b) outbound
 * sends route through their own WhatsApp/SMS instance instead of a
 * teammate's.
 *
 * The "prefer the viewer's row" rule already lives in
 * `dedupeChatsByConversationKey`; both helpers below stay DRY by delegating
 * to it.
 */

type ChatSeat = {
  id: string;
  user_id?: string | null;
  group_id?: string | null;
};

/**
 * Collapse a list of chat ids to one id per external conversation, preferring
 * the viewer's seat. Ids the viewer can't see (absent from `viewerChats`) are
 * dropped — they reference seats the viewer's Dexie store never synced, so
 * they're un-openable anyway.
 */
export function collapseChatIdsToViewerSeats<T extends ChatSeat>(
  ids: readonly string[],
  viewerChats: readonly T[],
  conversationKey: (chat: T) => string | null | undefined,
  viewerUserId: string | undefined,
): string[] {
  const byId = new Map(viewerChats.map((c) => [c.id, c] as const));
  const rows: T[] = [];
  for (const id of ids) {
    const row = byId.get(id);
    if (row) rows.push(row);
  }
  return dedupeChatsByConversationKey(rows, conversationKey, viewerUserId).map(
    (c) => c.id,
  );
}

/**
 * Given a chat id (any seat in a group conversation), return the viewer's
 * own seat id for that same conversation when one exists. Falls through to
 * the input id when:
 *   - the chat isn't in the viewer's visible set (unknown id — leave alone),
 *   - the chat has no `group_id` (no fan-out, only one seat exists), or
 *   - the chat is already owned by the viewer.
 *
 * Mirrors the per-id rewrite that `collapseChatIdsToViewerSeats` does at list
 * scope, so callers that resolve a single source id (e.g. `derivePreOrderSource`)
 * stay aligned with the popover collapse without duplicating the rule.
 */
export function resolveViewerSeat<T extends ChatSeat>(
  chatId: string,
  viewerChats: readonly T[],
  conversationKey: (chat: T) => string | null | undefined,
  viewerUserId: string | undefined,
): string {
  // Without a viewer id we have no rule for preferring one sibling over
  // another — `dedupeChatsByConversationKey` would just keep the first row
  // it sees, which could be a different sibling than the input id. Preserve
  // the input so anonymous callers (legacy tests, the brief window before
  // the auth provider resolves) never silently rewrite to someone else's
  // seat.
  if (!viewerUserId) return chatId;
  const target = viewerChats.find((c) => c.id === chatId);
  if (!target) return chatId;
  if (!target.group_id) return chatId;
  if (target.user_id === viewerUserId) return chatId;
  const targetKey = conversationKey(target);
  if (!targetKey) return chatId;
  const sameConversation = viewerChats.filter(
    (c) => c.group_id === target.group_id && conversationKey(c) === targetKey,
  );
  const [winner] = dedupeChatsByConversationKey(
    sameConversation,
    conversationKey,
    viewerUserId,
  );
  return winner?.id ?? chatId;
}
